import { apiFetch } from "./client";

/**
 * What a model produces. Orthogonal to `category` (chat vs reasoning), which
 * only qualifies text models. Pickers MUST filter on this — an image or
 * realtime model chosen as an agent's conversational model fails at runtime.
 */
export type ModelModality =
  | "text"
  | "image"
  | "audio"
  | "realtime"
  | "video"
  | "embedding"
  | "other";

/** Modalities an agent can currently be assigned a model for. */
export const ASSIGNABLE_MODALITIES: ModelModality[] = ["text", "image"];

export const MODALITY_LABELS: Record<ModelModality, string> = {
  text: "Text",
  image: "Image",
  audio: "Audio",
  realtime: "Realtime voice",
  video: "Video",
  embedding: "Embedding",
  other: "Unclassified",
};

export interface ModelCatalogueItem {
  id: string;
  provider: string;
  modelId: string;
  displayName: string;
  costPer1mInput: number | null;
  costPer1mOutput: number | null;
  contextWindow: number | null;
  maxOutputTokens: number | null;
  category: string;
  modality: ModelModality;
  capabilities: Record<string, string> | null;
  vaultKeyName: string | null;
  isDefault: boolean;
  enabled: boolean;
  availableToAllTenants: boolean;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface FoundryConnection {
  id: string;
  name: string;
  region: string | null;
  endpoint: string;
  resourceName: string | null;
  enabled: boolean;
  isDefault: boolean;
  keyConfigured?: boolean;
  maskedKey?: string;
}

export interface FoundryDeploymentDiscovered {
  deploymentName: string;
  modelId: string;
  displayName: string;
  modelPublisher: string;
  modelVersion: string;
  category: string;
  modality: ModelModality;
  capabilities: Record<string, string> | null;
  /** False when discovery couldn't place the deployment and parked it as "other". */
  modalityConfident: boolean;
  endpoint: string;
}

export const modelsApi = {
  list: () => apiFetch<ModelCatalogueItem[]>("/models"),

  listAll: () => apiFetch<ModelCatalogueItem[]>("/models?includeDisabled=true"),

  getById: (id: string) => apiFetch<ModelCatalogueItem>(`/models/${id}`),

  create: (data: Record<string, unknown>) =>
    apiFetch<ModelCatalogueItem>("/models", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Record<string, unknown>) =>
    apiFetch<ModelCatalogueItem>(`/models/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiFetch<void>(`/models/${id}`, { method: "DELETE" }),

  seed: () =>
    apiFetch<{ added: number; skipped: number }>("/models/seed", { method: "POST" }),

  setApiKey: (provider: string, value: string) =>
    apiFetch<{ success: boolean }>("/models/api-keys", {
      method: "PUT",
      body: JSON.stringify({ provider, value }),
    }),

  getApiKeyStatus: () =>
    apiFetch<Record<string, { configured: boolean; maskedValue?: string }>>("/models/api-keys"),

  testModel: (id: string) =>
    apiFetch<{ success: boolean; latencyMs?: number; response?: string; error?: string }>(`/models/test/${id}`, {
      method: "POST",
    }),

  discoverFoundry: (endpoint: string, apiKey?: string) =>
    apiFetch<{
      success: boolean;
      deployments?: Array<{
        deploymentName: string;
        modelId: string;
        displayName: string;
        modelPublisher: string;
        modelVersion: string;
        category: string;
        endpoint: string;
      }>;
      error?: string;
    }>("/models/foundry/discover", {
      method: "POST",
      body: JSON.stringify({ endpoint, ...(apiKey ? { apiKey } : {}) }),
    }),

  importFoundry: (models: Array<Record<string, string>>) =>
    apiFetch<{ added: number; skipped: number }>("/models/foundry/import", {
      method: "POST",
      body: JSON.stringify({ models }),
    }),

  syncFoundry: () =>
    apiFetch<{ success: boolean; added: number; updated: number; unchanged: number; total: number; error?: string }>("/models/foundry/sync", {
      method: "POST",
    }),

  syncModel: (agentId: string, modelId: string, fallbackModelId?: string | null) =>
    apiFetch<{
      success: boolean;
      /** Config was written + the runtime restarted. */
      synced?: boolean;
      /** The model was live-probed and actually serves (provider + creds OK). */
      verified?: boolean;
      /** The model the runtime confirmed it is serving. */
      activeModel?: string;
      error?: string;
      message?: string;
    }>(`/agents/${agentId}/sync-model`, {
      method: "POST",
      body: JSON.stringify({ modelId, ...(fallbackModelId !== undefined ? { fallbackModelId } : {}) }),
    }),

  /**
   * Models this tenant may use. The server DEFAULTS to modality=text — pass
   * "image" for an image picker, or "all" for admin views that want everything.
   */
  getAvailability: (companyId: string, modality: ModelModality | "all" = "text") =>
    apiFetch<{
      models: Array<ModelCatalogueItem & {
        access: "subscription" | "api_key" | "azure_foundry" | "none";
        accessReady: boolean;
        accessLabel: string;
        accessExpiresAt?: number;
      }>;
      providers: Record<string, {
        method: string;
        ready: boolean;
        label: string;
        expiresAt?: number;
      }>;
    }>(`/models/availability?companyId=${companyId}&modality=${modality}`),

  /** Assign non-text models to an agent. Pass "" to clear and inherit the tenant default. */
  setAgentMediaModels: (agentId: string, data: { imageModel?: string | null }) =>
    apiFetch<{ success: boolean; imageModel: string | null }>(
      `/agents/${agentId}/media-models`,
      { method: "PUT", body: JSON.stringify(data) },
    ),

  getAccess: (id: string) =>
    apiFetch<{ allTenants: boolean; tenantIds: string[] }>(`/models/${id}/access`),

  setAccess: (id: string, data: { allTenants: boolean; tenantIds: string[] }) =>
    apiFetch<{ allTenants: boolean; tenantIds: string[] }>(`/models/${id}/access`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
};

export interface TenantImageModel {
  id: string;
  modelId: string;
  displayName: string;
  provider: string;
  capabilities: Record<string, string> | null;
}

export const mediaApi = {
  /**
   * Image models granted to a tenant, plus which one would actually be used —
   * `resolved.source` says whether that came from the agent, the tenant default,
   * or from being the only granted model.
   */
  imageModels: (companyId: string, agentId?: string | null) =>
    apiFetch<{
      models: TenantImageModel[];
      resolved: {
        modelId: string;
        displayName: string;
        source: "agent" | "tenant" | "only-granted";
      } | null;
    }>(
      `/media/image-models?companyId=${companyId}${agentId ? `&agentId=${agentId}` : ""}`,
    ),
};

export interface ModelPolicy {
  defaultBackupModel: string | null;
  functionsModel: string | null;
  /** Model powering Scout, the in-platform help assistant. */
  assistantModel: string | null;
  models?: Array<{ id: string; label: string; provider: string }>;
}

export const modelPolicyApi = {
  get: () => apiFetch<ModelPolicy>("/admin/model-policy"),
  update: (patch: { defaultBackupModel?: string | null; functionsModel?: string | null; assistantModel?: string | null }) =>
    apiFetch<ModelPolicy>("/admin/model-policy", {
      method: "PUT",
      body: JSON.stringify(patch),
    }),
};

export const foundryConnectionsApi = {
  list: () => apiFetch<FoundryConnection[]>("/foundry-connections"),

  create: (data: { name: string; region?: string; endpoint: string; apiKey?: string }) =>
    apiFetch<FoundryConnection>("/foundry-connections", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (
    id: string,
    data: Partial<{ name: string; region: string; endpoint: string; enabled: boolean; isDefault: boolean; apiKey: string }>,
  ) =>
    apiFetch<FoundryConnection>(`/foundry-connections/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  remove: (id: string, force = false) =>
    apiFetch<void>(`/foundry-connections/${id}${force ? "?force=true" : ""}`, { method: "DELETE" }),

  discover: (id: string) =>
    apiFetch<{ success: boolean; deployments?: FoundryDeploymentDiscovered[]; error?: string }>(
      `/foundry-connections/${id}/discover`,
      { method: "POST" },
    ),

  import: (id: string, models: FoundryDeploymentDiscovered[]) =>
    apiFetch<{ added: number; updated: number }>(`/foundry-connections/${id}/import`, {
      method: "POST",
      body: JSON.stringify({ models }),
    }),

  sync: (id: string) =>
    apiFetch<{ success: boolean; added: number; updated: number; total: number; errors?: string[] }>(
      `/foundry-connections/${id}/sync`,
      { method: "POST" },
    ),
};
